import { FC, useEffect, useRef, useState } from 'react';
import './PtaMeetingDetailView.scss';
import images from '../../assets/images';
import { calculateDays, calculateDuration, checkIsTimeNow, formatTime, generateUniqueMeetingId, hasUserAlreadyJoined } from '../../utils/helper';
import { ALERT_MESSAGE_TWO_USERS, JITSI, PTA_ROLE, SCHOOL_ADMIN, TEACHER_ROLE } from '../../utils/constants';
import { getUserMedia } from '../../utils/webRTCHelper';
import { useAppSelector } from '../../redux/app/hooks';
import { useDispatch } from 'react-redux';
import { setCurrentPtaMeetId, setCurrentSessionDetail, setCurrentSessionRecordingDetail, setCurrentSessionTopic, setMeetComponentName } from '../../redux/slices/dashboardSlice';
import { useGlobalState } from '../../MeetContext/MeetContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Tooltip } from '@mui/material';
import WarningPopup from '../WarningPopup/WarningPopup';
import { PtaMeetType } from '../../pages/PtaMeetings/PtaMeetings';
import { UPDATE_MEETING_PARTICIPANT_COUNT, UPDATE_MEETING_STATUS } from '../../pages/AdminPta/api/PtaQuery';
import { useChimeMeeting } from '../../Chime/ChimeMeetingContext';
import ToasterWithMessage from '../toaster';

interface propsType{
    singleSessionDetail: PtaMeetType;
    fetchSessionDetails: () => void;
    sessionEnded: boolean;
}

const PtaMeetingDetailView:FC<propsType> = ({ singleSessionDetail, fetchSessionDetails, sessionEnded }) => {
  const { calendarSymbol, clockSymbol, sandWatch } = images;
  const [daysleftValue, setDaysleftValue] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState('');
  const [isOverflow, setIsOverflow] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  // const [sessionEnded, setSessionEnded] = useState<boolean>(false);
  const [isTeacherButtonClicked, setIsTeacherButtonClicked] = useState<boolean>(false);
  const [isStudentButtonClicked, setIsStudentButtonClicked] = useState<boolean>(false);
  const role = useAppSelector((state) => state.auth.role);
  const profile = useAppSelector((state) => state?.auth?.adminProfile);
  const dispatch = useDispatch();
  const {localStream, setIsMeetingEndByHost} = useGlobalState();
  const navigate = useNavigate();
  const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
  const location = useLocation();
  const { createMeeting, joinMeeting, setIsRecording, setRecordingStartTime } = useChimeMeeting();
  const meetService = useAppSelector((state) => state.auth.meetService)
  const { error } = ToasterWithMessage();
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [alreadyTwoUsersSameId, setAlreadyTwoUsersSameId] = useState<boolean>(false)
  const currentUserId = profile?.getCurrentUser?.nodes?.[0]?.uId;

  const [updateMeetStatus] = useMutation(UPDATE_MEETING_STATUS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
        fetchSessionDetails();
    },
    onError: (err) => {
      // setIsClickedStartButton(false);
      setIsTeacherButtonClicked(false);
    }
  });

  const [updateMeetParticipantCount] = useMutation(UPDATE_MEETING_PARTICIPANT_COUNT, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {},
    onError: (err) => {}
  });

  const getPreviewStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: true, audio: true});
      console.log("[Log] stream", stream, stream?.getTracks());
      stream.getTracks().forEach((track) => track.stop());
      return stream;
    } catch (err: any) {
      console.log("[Log] getPreviewStream Error", err);
      let errorMessage = "Unable to access your camera or microphone. Please check your device settings and try again.";
      if (err?.name === "NotAllowedError" || err?.name === "SecurityError") {
        errorMessage = "Permission denied. Please allow camera and microphone access.";
      } else if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        errorMessage = "No camera or microphone found. Please check your device.";
      } else if (err?.name === "NotReadableError" || err?.name === "HardwareAccessError") {
        errorMessage = "Another app is using your camera or microphone. Close the other app and try again.";
      } else if (err?.name === "OverconstrainedError") {
        errorMessage = "Your camera does not support the requested video resolution.";
      }
      setWarningMessage(errorMessage);
      if (role === SCHOOL_ADMIN) {
        setIsTeacherButtonClicked(false);
      } else {
        setIsStudentButtonClicked(false);
      }
      setOpenWarningPopup(true);
    }
  };

  const closeWarnPopup = () => {
    setOpenWarningPopup(false);
    setWarningMessage("");
  }
  
  const handleClickStartButton = async () => {
    setIsTeacherButtonClicked(true);
    if (meetService === JITSI) {
      handleStartMeeting();
    } else {
      const stream = await getPreviewStream();
      if (stream) {
        // setIsClickedStartButton(true);
        handleStartMeeting(stream);
      }
    }
  };

  const handleClickJoinButton = async () => {
    if (role === SCHOOL_ADMIN) {
      setIsTeacherButtonClicked(true);
    } else {
      setIsStudentButtonClicked(true);
    }
    if (meetService === JITSI) {
      handleJoinMeeting();
    } else {
      const stream = await getPreviewStream();
      if (stream) {
        handleJoinMeeting(stream);
      }
    }
  };

  const handleStartMeeting = async (stream?: any) => {
    dispatch(setMeetComponentName({ meetComponent: 'pta' }));
    if (meetService === JITSI) {
      // Check for Jitsi server url
      if (!singleSessionDetail?.serverUrl) {
        error("Meeting Server setup in progress.");
        setIsTeacherButtonClicked(false);
        return;
      }

      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      const userName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;
      const meetingId = generateUniqueMeetingId();

      try {
        // updateMeetStatus({
        //   variables: {
        //     updateInput: {
        //       mCode: meetingId,
        //       mStatus: "STARTED",
        //       hostId: userId,
        //     },
        //     mId: singleSessionDetail?.mId,
        //   }
        // });
        if (singleSessionDetail?.mId) {
          dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
          dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime }));
          dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
          dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
        }
        navigate(`/admin/pta/meeting?meetId=${meetingId}`, {
          state: { ...location?.state, meetingId: meetingId, userName: userName, isHost: true, isCreateMeeting: true, hostId: userId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
        });
      }
      catch (err) {
        console.log("Creating Meeting Error :", err);
        setIsTeacherButtonClicked(false);
      }
    }
    else {
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      await createMeeting(userId).then(async (response) => {
        console.log('[Log] createMeeting response', response);
        const meetId = response?.Meeting?.MeetingId;
        // const data = await response.json();
        if (meetId) {
          // const {meetId, user, host, createdAt} = data.meetInfo;
          try {
            // await InitialiseMeeting({
            //   meetId,
            //   avatar: getRandomColor(),
            //   currentUser: {
            //     userId: user.userId,
            //     avatarKey: user.avatarKey,
            //       userKey: user.userKey,
            //       userName: user.userName,
            //       preference: {
            //           audio: true,
            //           video: true,
            //           screen: false,
            //           hand: false
            //       },
            //       isPinned: {video: false, screen: false}
            //   },
            //   host:{
            //       hostId: host.hostId,
            //       hostKey: host.hostKey,
            //       hostName: host.hostName
            //   },
            //   createdAt
            // }, FormType.CREATE);
            // await updateMeetParticipantCount({
            //   variables: {
            //     input: {
            //       mId: singleSessionDetail?.mId,
            //       participantType: true
            //     }
            //   }
            // });
          } catch (err) {
            setIsTeacherButtonClicked(false);
          }
          updateMeetStatus({
            variables: {
              updateInput: {
                mCode: meetId,
                mStatus: "STARTED",
                hostId: userId,
              },
              mId: singleSessionDetail?.mId,
            }
          });
          if (singleSessionDetail?.mId) {
            dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
            dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime }));
            dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
            dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
          }
          navigate(`/admin/pta/meeting?meetId=${meetId}`, {
            state: { ...location?.state, sessionId: singleSessionDetail?.mId }
          });
        }
      }).catch((error) => {
        console.log("Creating Meeting Error :", error);
        setIsTeacherButtonClicked(false);
        stream.getAudioTracks()[0].stop();
        stream.getVideoTracks()[0].stop();
      });
    }
  };
  
  const handleJoinMeeting = async (stream?: any) => {
    dispatch(setMeetComponentName({ meetComponent: 'pta' }));
    if (meetService === JITSI) {
      // Check for Jitsi server url
      if (!singleSessionDetail?.serverUrl) {
        error("Meeting Server setup in progress.");
        if (role === SCHOOL_ADMIN) {
          setIsTeacherButtonClicked(false);
        } else {
          setIsStudentButtonClicked(false);
        }
        return;
      }
      
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      const userName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;

      if (singleSessionDetail?.mCode && singleSessionDetail?.hostId) {
        try {
          if (singleSessionDetail?.mId) {
            dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
            dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime }));
            dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
            setRecordingStartTime(singleSessionDetail?.recordStartTime);
            setIsRecording(singleSessionDetail?.isRecordStart);
            dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
          }
          if (role === PTA_ROLE) {
            navigate(`/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
              state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: false, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }

            });
          }
          else if (role === SCHOOL_ADMIN) {
            navigate(`/admin/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
              state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: singleSessionDetail?.hostId && userId === singleSessionDetail.hostId, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
            });
          }
          else if (role === TEACHER_ROLE) {
            navigate(`/teacher/pta/meeting?meetId=${singleSessionDetail?.mCode}`, {
              state: { ...location?.state, meetingId: singleSessionDetail?.mCode, userName: userName, isHost: false, isCreateMeeting: false, hostId: singleSessionDetail?.hostId, userId, meetComponent: 'pta', jitsiUrls: { jitsiMeetUrl: singleSessionDetail?.serverUrl, jitsiBotUrl: singleSessionDetail?.botUrl } }
            });
          }
        }
        catch (e) {
          if (role === SCHOOL_ADMIN) {
            setIsTeacherButtonClicked(false);
          } else {
            setIsStudentButtonClicked(false);
          }
          console.log("[Log] Error", e);
        }
      }
    }
    else {
      const userId = profile?.getCurrentUser?.nodes?.[0]?.uId;
      if (singleSessionDetail?.mCode && singleSessionDetail?.hostId) {
        await joinMeeting(singleSessionDetail.mCode, userId, singleSessionDetail.hostId)
          .then(async (response) => {
            console.log('[Log] joinMeeting response', response);
            const meetId = response?.meetingResponse?.Meeting?.MeetingId;
            // const data = await response.json();
            if (meetId) {
              // const {meetId, user, host, createdAt} = data.meetInfo;
              try {
                //   await InitialiseMeeting({
                //     meetId,
                //     avatar: getRandomColor(),
                //     currentUser: {
                //       userId: user.userId,
                //       avatarKey: user.avatarKey,
                //         userKey: user.userKey,
                //         userName: user.userName,
                //         preference: {
                //             audio: true,
                //             video: true,
                //             screen: false,
                //             hand: false
                //         },
                //         isPinned: {video: false, screen: false}
                //     },
                //     host:{
                //         hostId: host.hostId,
                //         hostKey: host.hostKey,
                //         hostName: host.hostName
                //     },
                //     createdAt
                // }, FormType.JOIN);
                // await updateMeetParticipantCount({
                //   variables: {
                //     input: {
                //       mId: singleSessionDetail?.mId,
                //       participantType: true
                //     }
                //   }
                // })
              } catch (err) {
                if (role === SCHOOL_ADMIN) {
                  setIsTeacherButtonClicked(false);
                } else {
                  setIsStudentButtonClicked(false);
                }
              }
              if (singleSessionDetail?.mId) {
                dispatch(setCurrentPtaMeetId({ currentPtaMeetId: singleSessionDetail?.mId }));
                dispatch(setCurrentSessionDetail({ startDate: singleSessionDetail?.mStartTime, endDate: singleSessionDetail?.mEndTime }));
                dispatch(setCurrentSessionRecordingDetail({ recordingStartedTime: singleSessionDetail?.recordStartTime, recordingStarted: singleSessionDetail?.isRecordStart }));
                setRecordingStartTime(singleSessionDetail?.recordStartTime);
                setIsRecording(singleSessionDetail?.isRecordStart);
                dispatch(setCurrentSessionTopic({ currentSessionTopic: singleSessionDetail?.mTopic }));
              }
              if (role === PTA_ROLE) {
                navigate(`/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
              else if (role === SCHOOL_ADMIN) {
                navigate(`/admin/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
              else if (role === TEACHER_ROLE) {
                navigate(`/teacher/pta/meeting?meetId=${meetId}`, {
                  state: { ...location?.state, sessionId: singleSessionDetail?.mId }
                });
              }
            }
          }).catch(() => {
            if (role === SCHOOL_ADMIN) {
              setIsTeacherButtonClicked(false);
            } else {
              setIsStudentButtonClicked(false);
            }
            stream.getAudioTracks()[0].stop();
            stream.getVideoTracks()[0].stop();
          })
      }
    } 
  };

  //To check the container is overflow
  const checkOverflow = () => {
    if (descriptionRef.current) {
      setIsOverflow(descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight);
    }
  };


  useEffect(() => {
    if(singleSessionDetail){
      const daysLeft = calculateDays(singleSessionDetail?.mStartTime, true);
      const startTimeFormatted = formatTime(singleSessionDetail?.mStartTime);
      const durationFormatted = calculateDuration(singleSessionDetail?.mStartTime, singleSessionDetail?.mEndTime);

      setDaysleftValue(daysLeft);
      setStartTime(startTimeFormatted);
      setDuration(durationFormatted);
      checkOverflow();
      const interval = setInterval(() => {
        fetchSessionDetails();
      }, 15000); // 60000 ms = 1 minute

      return () => clearInterval(interval);
    }
  },[singleSessionDetail])

  useEffect(() => {
    dispatch(setCurrentSessionDetail({startDate: null, endDate: null}));
    dispatch(setCurrentSessionRecordingDetail({recordingStartedTime: null, recordingStarted: false}));
    setIsMeetingEndByHost(false);
      //To set stop camera and microphone when initial render.
      if(localStream?.getVideoTracks()[0]){
            localStream.getVideoTracks()[0].enabled = false;
        }
        localStream?.getVideoTracks()[0]?.stop();
        if(localStream?.getAudioTracks()[0]){
            localStream.getAudioTracks()[0].enabled = false;
        }
        localStream?.getAudioTracks()[0]?.stop();
        setIsTeacherButtonClicked(false);
        setIsStudentButtonClicked(false);
  }, [])

  const isSessionStarted = singleSessionDetail?.mStatus === 'STARTED' ? true : false;
  const StudentJoinButtonCondition = (isSessionStarted && !sessionEnded && (singleSessionDetail?.mParticipantCount > 0));

  const isServerUrlReady = !singleSessionDetail?.meetingType || (singleSessionDetail?.meetingType === "JITSI" && singleSessionDetail?.serverUrl);

  useEffect(()=>{
      const alreadyJoined = hasUserAlreadyJoined(singleSessionDetail?.participantIds || [], currentUserId);
      setAlreadyTwoUsersSameId(alreadyJoined)
  
  },[singleSessionDetail?.participantIds])

  useEffect(()=>{
        if(sessionEnded)
        setAlreadyTwoUsersSameId(false)
      },[sessionEnded])

  return (
    <div className='singleSessionDetail'>
      <div className='leftSideSessionDetail'>
          <div className='sesstionTitle'>MEETING</div>
          <div className='listOfSessionDetail'>
            <div className='listsStyle'>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={calendarSymbol} alt='calendarIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{daysleftValue}</div>
                </div>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={clockSymbol} alt='clockIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{startTime}</div>
                </div>
                <div className='listStyle'>
                    <div className='imgCtn'>
                    <img src={sandWatch} alt='sandIcon' className='sideIconStyle'/>
                    </div>
                    <div className='textStyle'>{duration}</div>
                </div>
            </div>
          </div>
      </div>
      <div className='rightSideSessionDetail'>
          <div className='lessonTopicContainer'>
            <div className='heaaderStyle'>
              <div className='headTextStyle'>MEETING TOPIC:</div>
            </div>
            <Tooltip title={singleSessionDetail?.mTopic} disableHoverListener={!isOverflow}>
              <div
                className='descriptionStyle'
                ref={descriptionRef}
                onMouseEnter={checkOverflow}
                >
                {singleSessionDetail?.mTopic}
              </div>
            </Tooltip>
          </div>
          { (role === SCHOOL_ADMIN) ? (
            <button 
              className={`${(singleSessionDetail?.isActive && !sessionEnded && !isTeacherButtonClicked && isServerUrlReady) ? 'sessionStartButton' : 'notReadyButtonStyle'} ${(isSessionStarted && isServerUrlReady) ? 'sessionStartButton' : ''}`} 
              onClick={
                meetService === JITSI ?
                ((isSessionStarted) ? handleClickJoinButton : handleClickStartButton)
                : ((isSessionStarted && singleSessionDetail?.mParticipantCount === 0) ? handleClickStartButton : (isSessionStarted && singleSessionDetail?.mParticipantCount > 0) ? handleClickJoinButton : handleClickStartButton)
              } 
            > 
              {
                sessionEnded ? `Session Ended` : (singleSessionDetail?.isActive && !isSessionStarted && isServerUrlReady) ? `Start` :(isSessionStarted && isServerUrlReady)? 'Join' : 'Not Ready'
              }
            </button>) : (
            <div>
              <button 
                className={`${(StudentJoinButtonCondition && !isStudentButtonClicked && !alreadyTwoUsersSameId) ? 'sessionJoinButton' : 'notReadyStudentButtonStyle'}`} 
                onClick={handleClickJoinButton}
              >
                {
                    (sessionEnded ? `Session Ended` : (isSessionStarted && singleSessionDetail?.mParticipantCount === 0) ? `Session completed` : isSessionStarted ? `Join` : 'Not Ready')
                }
              </button>
              {(!isSessionStarted && !sessionEnded && singleSessionDetail?.isActive) && (
                <span className='sessionNotYetStarted'>
                  Host has not yet started the meeting
                </span>
              )}
            {alreadyTwoUsersSameId && (
            <span className='alert-msg'>
               {ALERT_MESSAGE_TWO_USERS}
            </span>
          )}
            </div>)
          }
      </div>
      <WarningPopup
        message={warningMessage}
        openWarningPopup={ openWarningPopup }
        closeWarnPopup={ closeWarnPopup }
      />
    </div>
  )
}

export default PtaMeetingDetailView;
